// Persistent SSH-shell sessions.
//
// The page Terminal.svelte mounts/unmounts as the user navigates, but a
// real shell session must outlive that lifecycle: typing into vim,
// switching to /dashboard for a quick check, navigating back — the
// shell should still be sitting at the same prompt.
//
// To get there we split lifetimes:
//
//   - WebSocket + history buffer live in the module-level session
//     (this file). They survive Svelte component unmounts.
//   - The xterm.js Terminal instance is recreated every time the page
//     is mounted. On attach we replay the buffered bytes into it; xterm
//     reconstructs the screen state from the ANSI byte stream.
//
// One session per server_id. The dropdown's "verbinden" creates the
// session lazily; "trennen" tears it down explicitly.

import { t } from "./i18n.svelte";

const SESSION_BUFFER_LIMIT = 200 * 1024; // 200 KB of raw byte history

// Lebenszeichen (Repo-Prüfung 21.10.2026, zweite Runde). Der Server
// schickt alle 30 s einen eigenen Text-Frame {"art":"puls"}
// (terminal_ws.go, nur mit ?puls=1), der nie ins Terminal geschrieben
// wird. Die Wache prüft wie beim Claude-Chat (claude/chat.svelte.ts) alle
// 20 s: kam 75 s lang nichts, auch kein Puls, ist die Leitung tot. Bis
// dahin stand eine abgerissene Shell weiter auf „verbunden".
const WACHE_TAKT = 20_000;
const STILLE_MAX = 75_000;

export type SessionState = "connecting" | "connected" | "closed";

/** Close-Code des Servers fuer "Vault-PIN abgelaufen" (terminal_ws.go). */
export const TERMINAL_GESPERRT = 4423;

type Listener = () => void;

export class TerminalSession {
  readonly serverId: string;
  state: SessionState = "connecting";
  error: string | null = null;
  /** Der Server hat die Shell geschlossen, weil die Freigabe der
   *  Vault-PIN abgelaufen ist (Close-Code 4423). Die Seite zeigt dann
   *  die PIN-Eingabe statt eines Verbindungsfehlers. */
  locked = false;
  // Last known PTY geometry — sent to the server on (re)open so the
  // shell agrees with whatever xterm is currently sized to.
  cols = 120;
  rows = 40;

  private ws: WebSocket | null = null;
  // Byte-history ring. Each chunk is what came in on one WS message;
  // we keep them as separate Uint8Arrays to avoid copying on append.
  private buffer: Uint8Array[] = [];
  private bufferBytes = 0;
  // Cursor: where live data goes when an xterm is attached. null while
  // off-page (data still goes into buffer).
  private cursor: ((data: Uint8Array) => void) | null = null;
  // Subscribers for state/error transitions. Component uses this to
  // drive its UI state ($state in svelte 5).
  private listeners = new Set<Listener>();
  // Wann zuletzt ein Frame kam (Ausgabe oder Puls), und die Wache darauf.
  private zuletzt = 0;
  private wache: ReturnType<typeof setInterval> | undefined;
  private wacheRunde = 0;

  constructor(serverId: string) {
    this.serverId = serverId;
  }

  /** Open the WebSocket. Idempotent on a live ws; on a closed session
   *  it starts fresh -- buffer wiped, neuer Verbindungs-Versuch. */
  connect(): void {
    if (this.ws) return;
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url =
      `${proto}//${window.location.host}/ws/terminal/${encodeURIComponent(this.serverId)}?puls=1`;
    // Fresh start: alten Buffer wegwerfen. Nutzer hat "verbinden"
    // geklickt = neue Shell, nicht Resume. Sonst bleibt der
    // [Verbindung geschlossen]-Banner aus dem letzten Run sichtbar
    // und mischt sich mit dem neuen Welcome-Banner.
    this.buffer = [];
    this.bufferBytes = 0;
    this.state = "connecting";
    this.error = null;
    this.locked = false;
    this.notify();

    const ws = new WebSocket(url);
    ws.binaryType = "arraybuffer";
    this.ws = ws;

    ws.onopen = () => {
      this.state = "connected";
      this.zuletzt = Date.now();
      this.bewache(ws);
      this.notify();
      // Tell the backend our geometry so the shell agrees with xterm.
      this.sendResize(this.cols, this.rows);
    };

    ws.onclose = (ev) => {
      const gesperrt = ev.code === TERMINAL_GESPERRT;
      this.beendet(
        gesperrt,
        gesperrt
          ? t("terminal.banner.gesperrt", "Gesperrt — die Vault-PIN ist abgelaufen")
          : t("terminal.banner.geschlossen", "Verbindung geschlossen"),
      );
    };

    ws.onerror = () => {
      // Bewusst KEIN error-State setzen: Browser feuern onerror vor
      // praktisch jedem onclose -- der eigentliche User-Signal ist der
      // [Verbindung geschlossen]-Banner aus onclose. Sonst hat man
      // einen sticky "WebSocket-Fehler"-Hinweis, der nichts erklaert.
    };

    ws.onmessage = (ev) => {
      this.zuletzt = Date.now();
      let data: Uint8Array;
      if (typeof ev.data === "string") {
        // Text-Frames sind der Puls (nur für die Wache, nie ins Terminal)
        // oder ein Fehler als JSON. Den zeigen wir im Strom, damit er
        // auch nach dem Wiederaufbau (replay) dasteht.
        let parsed: { error?: string; art?: string } | null = null;
        try {
          parsed = JSON.parse(ev.data);
        } catch {
          // Not JSON — treat as plain text.
        }
        if (parsed?.art === "puls") return;
        if (parsed?.error) {
          data = new TextEncoder().encode(`\r\n\x1b[31m${parsed.error}\x1b[0m\r\n`);
        } else {
          data = new TextEncoder().encode(ev.data);
        }
      } else {
        data = new Uint8Array(ev.data as ArrayBuffer);
      }
      this.append(data);
      this.cursor?.(data);
    };
  }

  /** Leitung zu: Zustand, Hinweis im Strom (sichtbar auch nach dem
   *  Wiederaufbau der Seite), Wache aus. */
  private beendet(gesperrt: boolean, text: string): void {
    clearInterval(this.wache);
    this.wache = undefined;
    this.state = "closed";
    this.locked = gesperrt;
    const farbe = gesperrt ? "33" : "31";
    const msg = new TextEncoder().encode(`\r\n\x1b[${farbe}m[${text}]\x1b[0m\r\n`);
    this.append(msg);
    this.cursor?.(msg);
    this.ws = null;
    this.notify();
  }

  /** Die Leitung bewachen: still über STILLE_MAX = tot. Anders als der
   *  Chat verbindet das Terminal danach NICHT selbst neu -- eine neue
   *  Leitung wäre eine neue Shell; „verbinden" bleibt beim Nutzer. */
  private bewache(ws: WebSocket): void {
    clearInterval(this.wache);
    this.wacheRunde = Date.now();
    this.wache = setInterval(() => {
      const jetzt = Date.now();
      // Stand der Takt selbst (Fenster im Hintergrund gedrosselt, Rechner
      // schlief), konnte die Seite auch keine Frames lesen: diese Runde
      // nicht urteilen, sondern ein frisches Fenster geben. Eine lebende
      // Shell zu schließen kostet mehr als beim Chat -- sie ist dann weg.
      const stand = jetzt - this.wacheRunde > 2 * WACHE_TAKT;
      this.wacheRunde = jetzt;
      if (this.ws !== ws || ws.readyState !== WebSocket.OPEN) return;
      if (stand) {
        this.zuletzt = Math.max(this.zuletzt, jetzt);
        return;
      }
      if (jetzt - this.zuletzt <= STILLE_MAX) return;
      // Tot: nicht auf onclose warten -- auf einer halb offenen Leitung
      // kommt das erst nach dem Schließ-Handschlag oder gar nicht.
      ws.onclose = null;
      ws.onmessage = null;
      ws.close();
      this.beendet(false, t("terminal.banner.still", "Verbindung geschlossen — 75 s ohne Lebenszeichen"));
    }, WACHE_TAKT);
  }

  /** Attach an xterm write callback. Replays the byte history first
   *  so the user sees the screen state they had before. */
  attach(write: (data: Uint8Array) => void): void {
    for (const chunk of this.buffer) write(chunk);
    this.cursor = write;
  }

  /** Detach the xterm write callback. Buffer keeps growing in the
   *  background; reattach replays the (possibly truncated) tail. */
  detach(): void {
    this.cursor = null;
  }

  /** Send raw stdin to the shell. */
  send(data: string | Uint8Array): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      // TS >= 5.7 macht TypedArrays generisch ueber den Buffer-Typ;
      // WebSocket.send verlangt ArrayBufferView<ArrayBuffer>, ein
      // generisches Uint8Array<ArrayBufferLike> (koennte SharedArrayBuffer
      // sein) passt nicht ohne Cast. Unsere Puffer sind immer ArrayBuffer.
      this.ws.send(data as string | Uint8Array<ArrayBuffer>);
    }
  }

  /** Send a PTY resize. Stored locally too so a reconnect can replay
   *  the geometry without a manual nudge from the page. */
  resize(cols: number, rows: number): void {
    this.cols = cols;
    this.rows = rows;
    this.sendResize(cols, rows);
  }

  /** Tear the session down. Closes the WS, clears the cursor, drops
   *  out of the registry. The component should dispose its xterm
   *  separately. */
  close(): void {
    clearInterval(this.wache);
    this.wache = undefined;
    if (this.ws) {
      // Setting onclose to null first avoids the "[Verbindung
      // geschlossen]"-banner appending to a buffer nobody will read.
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
    this.state = "closed";
    this.cursor = null;
    this.buffer = [];
    this.bufferBytes = 0;
    sessionMap.delete(this.serverId);
    this.notify();
  }

  /** Subscribe to state/error transitions. Returns an unsubscribe
   *  function — Svelte $effect cleanup uses it. */
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  private notify(): void {
    for (const fn of this.listeners) fn();
    // Registry-Subscriber (z.B. der Toolbar-Indikator) lauschen auf
    // jede State-Transition irgendeiner Session.
    notifyRegistry();
  }

  private append(chunk: Uint8Array): void {
    this.buffer.push(chunk);
    this.bufferBytes += chunk.byteLength;
    // Drop oldest chunks until we're under the limit. Keep at least
    // one chunk so a single huge paste doesn't wipe everything.
    while (this.bufferBytes > SESSION_BUFFER_LIMIT && this.buffer.length > 1) {
      const drop = this.buffer.shift();
      if (drop) this.bufferBytes -= drop.byteLength;
    }
  }

  private sendResize(cols: number, rows: number): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ resize: { cols, rows } }));
    }
  }
}

// Module-level registry: one session per server_id, lives until
// either close() or full page reload.
const sessionMap = new Map<string, TerminalSession>();

// Registry-level subscribers: get pinged on add, remove, AND every
// state transition of any session. The toolbar uses this to drive the
// "Active Terminal"-Indikator without polling.
const registryListeners = new Set<Listener>();

function notifyRegistry(): void {
  for (const fn of registryListeners) fn();
}

/** subscribeSessionRegistry triggers fn on any registry change (new
 *  session, removed session, or any session's state transition).
 *  Returns the unsubscribe handle. */
export function subscribeSessionRegistry(fn: Listener): () => void {
  registryListeners.add(fn);
  return () => {
    registryListeners.delete(fn);
  };
}

/** activeSessions returns a snapshot of all sessions whose WS is
 *  alive (state !== "closed"). The toolbar shows a quick-jump icon
 *  whenever this is non-empty. */
export function activeSessions(): TerminalSession[] {
  const out: TerminalSession[] = [];
  for (const s of sessionMap.values()) {
    if (s.state !== "closed") out.push(s);
  }
  return out;
}

/** getOrCreateSession returns the existing session for serverId or
 *  creates an unconnected one. The caller is expected to call
 *  connect() to actually open the WebSocket. */
export function getOrCreateSession(serverId: string): TerminalSession {
  let s = sessionMap.get(serverId);
  if (!s) {
    s = new TerminalSession(serverId);
    sessionMap.set(serverId, s);
    notifyRegistry();
  }
  return s;
}

/** getSession returns the session if one exists, else undefined. Used
 *  to check "is this server already connected?" without creating. */
export function getSession(serverId: string): TerminalSession | undefined {
  return sessionMap.get(serverId);
}

